import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye, RotateCcw } from 'lucide-react';
import {
  BoutonMint,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { ModaleRenvoiConvention } from '../components/documents/ValidationConvention';
import { useToast } from '../components/ui/useToast';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { dateReunion } from '../lib/useFormateurs';
import { urlDocumentGenere } from '../lib/useDocuments';
import {
  useConventions,
  useValiderConvention,
  type EtatConvention,
  type LigneConvention,
  type ReponseConventions,
} from '../lib/useConventions';

type Vue = EtatConvention | 'TOUTES';

const VUES: readonly Vue[] = ['A_VALIDER', 'VALIDEE', 'SIGNEE', 'BROUILLON', 'TOUTES'];

const LIBELLES_VUE: Record<Vue, string> = {
  A_VALIDER: 'À valider',
  VALIDEE: 'Validées, à signer',
  SIGNEE: 'Signées',
  BROUILLON: 'Brouillons',
  TOUTES: 'Toutes',
};

const MESSAGES_VIDE: Record<Vue, string> = {
  A_VALIDER: 'Aucune convention n’attend votre validation dans ce périmètre.',
  VALIDEE: 'Aucune convention validée n’attend son exemplaire signé.',
  SIGNEE: 'Aucune convention signée dans ce périmètre.',
  BROUILLON: 'Aucune convention en brouillon.',
  TOUTES: 'Aucune convention de formation dans ce périmètre.',
};

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/** Recherche sans casse ni accent : « Begue » trouve « Bègue ». */
function cleRecherche(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function vueDeLAdresse(valeur: string | null): Vue {
  return VUES.includes(valeur as Vue) ? (valeur as Vue) : 'A_VALIDER';
}

function compte(donnees: ReponseConventions, vue: Vue): number {
  return vue === 'TOUTES' ? donnees.lignes.length : donnees.lignes.filter((ligne) => ligne.etat === vue).length;
}

/**
 * Validation des conventions de formation (phase 5, TF-430) : la direction
 * valide ce que le pôle administratif a soumis, ou le renvoie en brouillon
 * avec un motif ; puis suit les conventions validées jusqu'à leur signature.
 *
 * L'écran ne décide rien : état, refus et compteurs viennent de
 * `/api/conventions`. La soumission, elle, se fait depuis le dossier.
 */
export function DirConventionsPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useConventions(site);
  const [parametres, setParametres] = useSearchParams();
  const vue = vueDeLAdresse(parametres.get('etat'));
  const [recherche, setRecherche] = useState('');
  const [formation, setFormation] = useState('');
  const [apercu, setApercu] = useState<LigneConvention | null>(null);
  const [aRenvoyer, setARenvoyer] = useState<LigneConvention | null>(null);
  const compteurs = data?.compteurs;

  const changerVue = (suivante: Vue): void => {
    setParametres(
      (avant) => {
        const prochains = new URLSearchParams(avant);
        if (suivante === 'A_VALIDER') prochains.delete('etat');
        else prochains.set('etat', suivante);
        return prochains;
      },
      { replace: true },
    );
  };

  const lignes = useMemo(() => {
    if (!data) return [];
    const terme = cleRecherche(recherche);
    return data.lignes.filter((ligne) => {
      if (vue !== 'TOUTES' && ligne.etat !== vue) return false;
      if (formation && ligne.formation?.code !== formation) return false;
      if (!terme) return true;
      return [ligne.apprenant.nom, ligne.apprenant.prenom, ligne.contrat?.employeur ?? ''].some((champ) =>
        cleRecherche(champ).includes(terme),
      );
    });
  }, [data, vue, recherche, formation]);
  const filtreActif = recherche.trim() !== '' || formation !== '';

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Conventions"
        sousTitre="Conventions de formation · validées par la direction avant toute transmission ou signature"
        actions={<BarreSites />}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="À valider"
          valeur={compteurs?.aValider ?? '—'}
          ton={compteurs && compteurs.aValider > 0 ? 'warm' : 'navy'}
          detail={
            compteurs === undefined || compteurs.aValider === 0
              ? 'Aucune convention en attente'
              : compteurs.plusAncienneJours === null
                ? 'Date de soumission non connue'
                : compteurs.plusAncienneJours === 0
                  ? 'La plus ancienne soumise aujourd’hui'
                  : `La plus ancienne soumise il y a ${pluriel(compteurs.plusAncienneJours, 'jour')}`
          }
        />
        <Compteur
          libelle="Validées, à signer"
          valeur={compteurs?.validees ?? '—'}
          ton="blue"
          detail="Exemplaire signé attendu"
        />
        <Compteur libelle="Signées" valeur={compteurs?.signees ?? '—'} ton="mint" detail="Exemplaire signé contrôlé" />
        <Compteur
          libelle="Brouillons"
          valeur={compteurs?.brouillons ?? '—'}
          ton="navy"
          detail={
            compteurs && compteurs.renvoyees > 0
              ? `dont ${pluriel(compteurs.renvoyees, 'renvoyée')} par la direction`
              : 'À soumettre par le pôle administratif'
          }
        />
      </div>

      {isLoading && <Chargement libelle="Chargement des conventions…" />}
      {error && <MessageErreur message={messageErreur(error, 'Les conventions n’ont pas pu être chargées.')} />}

      {data && (
        <>
          <Onglets
            etiquette="Conventions"
            onglets={VUES.map((cle) => ({ cle, libelle: LIBELLES_VUE[cle], compte: compte(data, cle) }))}
            actif={vue}
            onChanger={changerVue}
            idPanneau="panneau-conventions"
            actions={
              <>
                <ChampRecherche
                  valeur={recherche}
                  onChange={setRecherche}
                  etiquette="Rechercher une convention"
                  placeholder="Apprenant, employeur…"
                />
                <ChampFiltre valeur={formation} onChange={setFormation} etiquette="Formation">
                  <option value="">Toutes les formations</option>
                  {data.options.formations.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.titre}
                    </option>
                  ))}
                </ChampFiltre>
                {filtreActif && (
                  <button
                    type="button"
                    onClick={() => {
                      setRecherche('');
                      setFormation('');
                    }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    Réinitialiser
                  </button>
                )}
              </>
            }
          />
          <PanneauOnglet id="panneau-conventions" actif={vue}>
            <ListeConventions
              lignes={lignes}
              vide={filtreActif ? 'Aucune convention ne correspond aux filtres.' : MESSAGES_VIDE[vue]}
              onVoir={setApercu}
              onRenvoyer={setARenvoyer}
            />
          </PanneauOnglet>
        </>
      )}

      {apercu && (
        <VisionneusePiece
          ouverte
          libellePiece={`Convention de formation — ${apercu.apprenant.prenom} ${apercu.apprenant.nom}`}
          nomFichier={`convention-${apercu.apprenant.nom.toLowerCase()}.pdf`}
          urlBase={urlDocumentGenere(apercu.id)}
          onFermer={() => setApercu(null)}
        />
      )}
      {aRenvoyer && (
        <ModaleRenvoiConvention
          documentId={aRenvoyer.id}
          apprenantId={aRenvoyer.apprenant.id}
          intitule={`la convention de ${aRenvoyer.apprenant.prenom} ${aRenvoyer.apprenant.nom}`}
          onFermer={() => setARenvoyer(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Liste
// ---------------------------------------------------------------------------

const CLASSE_TH = 'px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4';
const CLASSE_TD = 'px-3 py-3 align-top xl:px-4';

function ListeConventions({
  lignes,
  vide,
  onVoir,
  onRenvoyer,
}: {
  lignes: LigneConvention[];
  vide: string;
  onVoir: (ligne: LigneConvention) => void;
  onRenvoyer: (ligne: LigneConvention) => void;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  useEffect(() => setPage(1), [taillePage, lignes.length]);

  if (lignes.length === 0) {
    return <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">{vide}</p>;
  }

  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card">
      {/* Sous l'ordinateur, une carte par convention : le tableau y défilerait de côté. */}
      <ul className="grid grid-cols-1 divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0 xl:hidden">
        {affichees.map((ligne) => (
          <li key={ligne.id} className="flex min-w-0 flex-col gap-3 border-gray-100 p-4 md:border-b md:odd:border-r">
            <Identite ligne={ligne} />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Formation ligne={ligne} />
              <Etat ligne={ligne} />
            </div>
            <Circuit ligne={ligne} />
            <Gestes ligne={ligne} onVoir={onVoir} onRenvoyer={onRenvoyer} />
          </li>
        ))}
      </ul>
      <div className="relative hidden overflow-x-auto xl:block">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="border-b border-gray-100 bg-gray-50">
            <tr>
              {['Apprenant', 'Formation et contrat', 'Circuit', 'État', ''].map((colonne, rang) => (
                <th key={rang} className={CLASSE_TH}>
                  {colonne || <span className="sr-only">Actions</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {affichees.map((ligne) => (
              <tr key={ligne.id} className="transition hover:bg-gray-50">
                <td className={`${CLASSE_TD} max-w-[200px]`}>
                  <Identite ligne={ligne} />
                </td>
                <td className={`${CLASSE_TD} max-w-[220px]`}>
                  <Formation ligne={ligne} />
                </td>
                <td className={`${CLASSE_TD} max-w-[280px]`}>
                  <Circuit ligne={ligne} />
                </td>
                <td className={CLASSE_TD}>
                  <Etat ligne={ligne} />
                </td>
                <td className={`${CLASSE_TD} w-[150px]`}>
                  <Gestes ligne={ligne} onVoir={onVoir} onRenvoyer={onRenvoyer} empile />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
        total={lignes.length}
        affiches={affichees.length}
        taillePage={taillePage}
        onPage={setPage}
        onChangerTaille={setTaillePage}
        uniteLabel="convention"
      />
    </div>
  );
}

function Detail({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-[11px] text-codialis-gray">{children}</div>;
}

function Libelle({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wider text-codialis-gray xl:hidden">{children}</div>
  );
}

function Identite({ ligne }: { ligne: LigneConvention }): JSX.Element {
  return (
    <div className="flex min-w-0 items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-codialis-sky to-codialis-blue text-xs font-bold text-white">
        {`${ligne.apprenant.prenom.charAt(0)}${ligne.apprenant.nom.charAt(0)}`.toUpperCase()}
      </span>
      <div className="min-w-0">
        <div className="truncate font-semibold text-codialis-navy">
          {ligne.apprenant.prenom} {ligne.apprenant.nom}
        </div>
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <PastilleSite site={ligne.site.code} />
          <Pastille texte={ligne.mode} ton="neutral" />
          {ligne.importee && <Pastille texte="Importée signée" ton="info" />}
        </div>
      </div>
    </div>
  );
}

function Formation({ ligne }: { ligne: LigneConvention }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-xs">
      <Libelle>Formation et contrat</Libelle>
      <div className="font-medium text-codialis-navy">{ligne.formation?.titre ?? 'Formation à préciser'}</div>
      {ligne.contrat ? (
        <>
          <Detail>{ligne.contrat.employeur}</Detail>
          <Detail>
            Du {dateReunion(ligne.contrat.debut)} au {dateReunion(ligne.contrat.fin)}
          </Detail>
        </>
      ) : (
        <Detail>Sans contrat rattaché</Detail>
      )}
    </div>
  );
}

function Circuit({ ligne }: { ligne: LigneConvention }): JSX.Element {
  const par = (auteur: string | null): string => (auteur ? ` par ${auteur}` : '');
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <Libelle>Circuit</Libelle>
      <Detail>
        {ligne.importee ? 'Importée' : 'Générée'} le {dateReunion(ligne.genereLe)}
      </Detail>
      {ligne.soumission && (
        <Detail>
          Soumise le {dateReunion(ligne.soumission.le)}
          {par(ligne.soumission.par)}
        </Detail>
      )}
      {ligne.validation && (
        <Detail>
          Validée le {dateReunion(ligne.validation.le)}
          {par(ligne.validation.par)}
        </Detail>
      )}
      {ligne.transmission && (
        <Detail>
          Transmise le {dateReunion(ligne.transmission.le)}
          {ligne.transmission.destinataire ? ` à ${ligne.transmission.destinataire}` : ''}
        </Detail>
      )}
      {ligne.renvoi && (ligne.etat === 'BROUILLON' || ligne.etat === 'A_VALIDER') && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          <span className="font-semibold">
            Renvoyée le {dateReunion(ligne.renvoi.le)}
            {par(ligne.renvoi.par)}
          </span>
          <span className="whitespace-pre-line break-words"> · « {ligne.renvoi.motif} »</span>
        </p>
      )}
    </div>
  );
}

function Etat({ ligne }: { ligne: LigneConvention }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1 text-xs">
      <Libelle>État</Libelle>
      <Pastille texte={ligne.libelle} ton={ligne.ton} />
      {ligne.attenteJours !== null && (
        <Detail>
          {ligne.attenteJours === 0
            ? 'Soumise aujourd’hui'
            : `En attente depuis ${pluriel(ligne.attenteJours, 'jour')}`}
        </Detail>
      )}
    </div>
  );
}

function Gestes({
  ligne,
  onVoir,
  onRenvoyer,
  empile = false,
}: {
  ligne: LigneConvention;
  onVoir: (ligne: LigneConvention) => void;
  onRenvoyer: (ligne: LigneConvention) => void;
  empile?: boolean;
}): JSX.Element {
  const toast = useToast();
  const valider = useValiderConvention();
  const confirmer = (): void =>
    valider.mutate(
      { documentId: ligne.id, apprenantId: ligne.apprenant.id },
      {
        onSuccess: (resultat) => toast(resultat.message),
        onError: (refus) => toast(messageErreur(refus, 'La convention n’a pas pu être validée.')),
      },
    );

  const consultation = (
    <>
      <button
        type="button"
        onClick={() => onVoir(ligne)}
        className={
          empile
            ? 'inline-flex min-h-[36px] items-center gap-1 text-xs font-semibold text-codialis-navy hover:underline [@media(pointer:coarse)]:min-h-[44px]'
            : 'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50'
        }
      >
        <Eye className="h-4 w-4" aria-hidden />
        Voir
      </button>
      <Link
        to={`/adm-dossier/${ligne.apprenant.id}`}
        className={
          empile
            ? 'inline-flex min-h-[36px] items-center whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]'
            : 'inline-flex min-h-[44px] items-center justify-center whitespace-nowrap rounded-xl px-3 text-sm font-semibold text-codialis-sky hover:underline'
        }
      >
        {empile ? 'Dossier' : 'Ouvrir le dossier'}
      </Link>
    </>
  );

  return (
    <div className={empile ? 'flex flex-col items-stretch gap-2' : 'mt-auto flex flex-wrap gap-2 pt-1'}>
      {ligne.refusValidation === null && (
        <BoutonMint onClick={confirmer} disabled={valider.isPending}>
          {valider.isPending ? 'Validation…' : 'Valider'}
        </BoutonMint>
      )}
      {ligne.refusRenvoi === null && <BoutonSecondaire onClick={() => onRenvoyer(ligne)}>Renvoyer</BoutonSecondaire>}
      {empile ? <div className="flex items-center justify-between gap-2">{consultation}</div> : consultation}
    </div>
  );
}
